import { Suspense, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useAuthStore from '@/stores/authStore';
import SettingsShell from './settings/SettingsShell';
import { SettingsHead, SettingsSkeleton } from './settings/ui';
import {
  LEGACY_SECTION_ALIASES,
  resolveSectionId,
  visibleSections,
  type SettingsSectionDef,
} from './settings/registry';

/**
 * Einstellungen — Container.
 *
 * Die Hülle, die Navigation und die Inhalts-Primitive liegen in
 * `components/settings/`. Hier bleibt nur: Abschnitt aus der URL auflösen,
 * Rechte prüfen, Abschnitt lazy rendern.
 *
 * Kanonische Adresse ist `/settings/<id>`. Das frühere `?tab=` wird einmalig
 * umgeschrieben — vorher existierten beide Schemata nebeneinander und haben
 * sich gegenseitig überschrieben.
 */
export default function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user: currentUser } = useAuthStore();

  const role = currentUser?.role;
  const sections = useMemo(() => visibleSections(role), [role]);

  const legacyTab = new URLSearchParams(location.search).get('tab');
  const requested = legacyTab || params.section;
  const activeId = resolveSectionId(requested ?? undefined, role);

  // Altes `?tab=` sofort auf den Pfad heben.
  useEffect(() => {
    if (legacyTab && activeId) {
      navigate(`/settings/${activeId}`, { replace: true });
    }
  }, [legacyTab, activeId, navigate]);

  // Unbekannter, aliasierter oder nicht erlaubter Abschnitt → auflösen.
  useEffect(() => {
    if (legacyTab || !activeId) return;
    const alias = params.section ? LEGACY_SECTION_ALIASES[params.section] : undefined;
    if (!params.section || alias || params.section !== activeId) {
      navigate(`/settings/${activeId}`, { replace: true });
    }
  }, [params.section, activeId, legacyTab, navigate]);

  const active: SettingsSectionDef | undefined = sections.find((section) => section.id === activeId);

  if (!active) {
    return (
      <div className="st-shell">
        <div className="st-main">
          <div className="st-page">
            <SettingsHead
              title={t('common:state.accessDenied', 'Zugriff verweigert')}
              description={t(
                'settings:nav.noAccess',
                'Für Ihre Rolle sind keine Einstellungen freigegeben.',
              )}
            />
          </div>
        </div>
      </div>
    );
  }

  const SectionComponent = active.Component;

  return (
    <SettingsShell
      sections={sections}
      activeId={active.id}
      onSelect={(id) => navigate(`/settings/${id}`)}
      onClose={() => navigate('/dashboard')}
    >
      <div className="st-page">
        <SettingsHead
          title={t(active.titleKey, active.titleFallback)}
          description={t(active.descKey, active.descFallback)}
        />
        <Suspense fallback={<SettingsSkeleton />}>
          <SectionComponent />
        </Suspense>
      </div>
    </SettingsShell>
  );
}
